import { Data } from "@core/content/types";
import { configureCreatorContentStore, getCreatorPayload } from "./contentStore";
import {
  extractArtworkInputsFromPayload,
  generateImageTargets,
  type ImageTargetDescriptor,
} from "@webcore/core/targets/target.image.8w";

// Cache for generated image targets (populated lazily)
let generatedImageTargets: ImageTargetDescriptor[] | null = null;
let imageTargetsPromise: Promise<ImageTargetDescriptor[]> | null = null;

export {
  ContentData,
  MicrositeData,
  INITIAL_CONTENT_ID,
  initializeCreatorData,
  getContent,
} from "./contentStore";

const getImageTargetSourcePayload = () => {
  if (typeof window !== "undefined" && (window as any).__ARTWORK_DATA__) {
    return (window as any).__ARTWORK_DATA__;
  }

  return getCreatorPayload();
};

// Set this once per project:
// - Local file: new URL("./creatorData.json", import.meta.url).href
// - URL:        "https://launch.eyejackapp.com/Collection-... ?j=1"
export const CREATOR_DATA_SOURCE =
  "https://launch.eyejackapp.com/Artwork-babd30fc-8a8b-49d2-b90d-64c28daa6fb8?j=1";

// Manual content for non-creator items (scenes, models, etc.)
// Use targetName to match the 8th Wall target and override creator data (e.g. add custom scale)
export const MANUAL_CONTENT: Data[] = [];

configureCreatorContentStore({
  creatorDataSource: CREATOR_DATA_SOURCE,
  manualContent: MANUAL_CONTENT,
});

/**
 * Get image target data with client-side image generation (async version).
 * Generates target images in the browser using Canvas API.
 */
export const getImageTargetDataAsync = async (): Promise<ImageTargetDescriptor[]> => {
  // Check if pre-built targets are injected
  if ((window as any).__IMAGE_TARGET_DATA__?.length > 0) {
    return (window as any).__IMAGE_TARGET_DATA__;
  }

  // Check if already generated
  if (generatedImageTargets) {
    return generatedImageTargets;
  }

  // Return existing promise if generation is in progress
  if (imageTargetsPromise) {
    return imageTargetsPromise;
  }

  // Generate client-side using Canvas API
  const artworkPayload = getImageTargetSourcePayload();
  const artworkInputs = extractArtworkInputsFromPayload(artworkPayload);
  imageTargetsPromise = generateImageTargets(artworkInputs).then((targets) => {
    generatedImageTargets = targets;
    return targets;
  });

  return imageTargetsPromise;
};

export const GEOGATE_CONFIG = {
  enabled: false,
  lat: "-33.886",
  lon: "151.201",
  radiusKm: 99999,
} as const;
